import { FC, useEffect, useRef, useState } from 'react';
import { RootState } from '../../redux/AppReducer';
import { useSelector } from 'react-redux';
import { ButtonComponent } from '../UI/ButtonComponent/ButtonComponent';
import {
	ISortByOption,
	ITrackingEvent,
	ScreenType,
	TrackingEventStatus,
	sortOptionType,
	TrackingEventType
} from '../../common/view-model';
import { cloneDeep } from '@apollo/client/utilities';
import './SortProductsComponent.scss';
import { isCurrentUserAnonymous } from '../../helpers/authenticationHelper';
import { UserActions } from '../../redux/UserActions';
import { publicProductDiscoveryActions } from '../../common/constants';
import { v4 as uuidv4 } from 'uuid';
import { handleTracking } from '../../helpers/handelTracking';
import { useAppDispatch } from '../../redux/store';

interface ISortProductsComponentProps {
	setSortBy: (sortBy: ISortByOption) => void;
	availableSorts: ISortByOption[];
	isFavoritesPage: boolean;
}
const SortProductsComponent: FC<ISortProductsComponentProps> = ({
	setSortBy,
	availableSorts,
	isFavoritesPage
}) => {
	const dispatch = useAppDispatch();
	const { sortBySelected, favoriteSortBySelected } = useSelector(
		(state: RootState) => state.appState?.filterState || {}
	);
	const currentEvent = useSelector(
		(state: RootState) => state.appState.storeState?.getCurrentEvent
	);
	const {
		user_type: userType,
		id: userId,
		favoriteProducts
	} = useSelector((state: RootState) => state.appState.userState || {});
	const deviceScreen = useSelector((state: RootState) => state.appState?.deviceScreen);
	const hasFavorites = favoriteProducts?.length! > 0;
	const currentEventSortOptionId = '3';
	const [open, setOpen] = useState<boolean>(false);
	const [sortOptions, setSortOptions] = useState<ISortByOption[]>(cloneDeep([...availableSorts]));
	const noFavoritesText = '(no products favorited yet)';
	const [currentSortBy, setCurrentSortBy] = useState<ISortByOption | undefined>(
		isFavoritesPage ? favoriteSortBySelected : sortBySelected
	);
	const [leftAling, setLeftAling] = useState<boolean>(
		currentSortBy?.sortType === sortOptionType.FAVORITES ||
			currentSortBy?.sortType === sortOptionType.CURRENT_EVENT ||
			currentSortBy?.sortType === sortOptionType.MOST_RELEVANT
	);
	const registerUserClickEvent = (event: string) =>
		dispatch(UserActions.registerUserClickEvent(event));

	const optionToEventType: Record<string, TrackingEventType> = {
		'Alphabetically (a - z)': TrackingEventType.CLICK_ON_FAVORITES_SORT_BY_ALPHABETICAL,
		'HowGood Estimate': TrackingEventType.CLICK_ON_FAVORITES_SORT_BY_HOWGOOD,
		'Newtopia Now': TrackingEventType.CLICK_ON_FAVORITES_SORT_BY_NEWTOPIA,
		'Expo West': TrackingEventType.CLICK_ON_FAVORITES_SORT_BY_EXPOWEST
	};

	const handleOptionClick = (option: ISortByOption) => {
		console.log('SORTING', option.name);
		if (!isCurrentUserAnonymous()) {
			const trackingPayload: ITrackingEvent = {
				transactionId: uuidv4(),
				status: TrackingEventStatus.SUCCESS,
				type: optionToEventType[(option.name ?? '').trim()],
				timeStamp: new Date().toISOString(),
				entityId: option.id,
				eventPayload: {
					id: option.id,
					name: option.name!,
					type: option.sortType.toString(),
					url: window.location.href,
					tags: [],
					metadata: {
						user_type: userType,
						user_id: userId,
						environment: import.meta.env.VITE_ENV_NAME
					}
				}
			};
			handleTracking(trackingPayload);
		}
		if (isCurrentUserAnonymous()) {
			registerUserClickEvent(publicProductDiscoveryActions.CLICK_SORT_BY);
		}
		if (
			(option.sortType === sortOptionType.FAVORITES && hasFavorites) ||
			option.sortType !== sortOptionType.FAVORITES
		) {
			setCurrentSortBy(option);
			setSortBy(option);
			setOpen(false);
		}
	};
	const sortButtonRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!hasFavorites) {
			const newSortOptions: ISortByOption[] = cloneDeep([...availableSorts]);
			newSortOptions.forEach(option => {
				if (option.sortType === sortOptionType.FAVORITES) {
					option.name = `${option.name} ${noFavoritesText}`;
				}
			});
			setSortOptions(cloneDeep(newSortOptions!));
		} else {
			setSortOptions(cloneDeep([...availableSorts]));
		}
		if (currentSortBy?.sortType === sortOptionType.FAVORITES && !hasFavorites) {
			setSortBy(availableSorts.find(option => option.sortType === sortOptionType.ALPHABETICAL)!);
		}
		if (!currentEvent) {
			setSortOptions(sortOptions.filter(option => option.id !== currentEventSortOptionId));
		} else {
			const newSortOptions: ISortByOption[] = [...availableSorts];
			const currentEventOption = newSortOptions.find(
				option => option.id === currentEventSortOptionId
			);
			if (currentEventOption && currentEvent) {
				currentEventOption.name = currentEvent?.label || currentEvent?.name;
			}
			setSortOptions(newSortOptions);
		}
	}, [favoriteProducts, hasFavorites, availableSorts, currentSortBy, currentEvent]);
	useEffect(() => {
		setLeftAling(
			currentSortBy?.sortType === sortOptionType.FAVORITES ||
				currentSortBy?.sortType === sortOptionType.CURRENT_EVENT ||
				currentSortBy?.sortType === sortOptionType.MOST_RELEVANT
		);
	}, [currentSortBy]);

	useEffect(() => {
		setCurrentSortBy(isFavoritesPage ? favoriteSortBySelected : sortBySelected);
	}, [currentSortBy, favoriteSortBySelected, isFavoritesPage, sortBySelected]);

	useEffect(() => {
		function handleClickSortButton(e: MouseEvent) {
			if (sortButtonRef.current && !e.composedPath().includes(sortButtonRef.current)) {
				setOpen(false);
			}
		}

		window.addEventListener('click', handleClickSortButton);
		return () => window.removeEventListener('click', handleClickSortButton);
	}, []);

	return (
		<div data-testid='sort-products-component' className='sort-products-container'>
			<div ref={sortButtonRef} className='sort-button-container'>
				<ButtonComponent
					style={`sort-by-btn ${leftAling ? 'left-align' : ''} ${open ? 'open-sort' : ''}`}
					text={deviceScreen !== ScreenType.MOBILE ? currentSortBy?.name! : ''}
					id={`sort-by${currentSortBy?.id}`}
					onClick={() => setOpen(prev => !prev)}
					icon={<i className={open ? 'icon ri-arrow-up-s-line' : 'icon ri-arrow-down-s-line'}></i>}
					iconPosition='end'
				/>
			</div>

			{open && (
				<div data-testid='sort-by-options' className='sort-by-options-container'>
					{sortOptions?.map((option, index: number) => (
						<span
							key={index}
							className={`sort-option ${option.id === currentSortBy?.id ? 'sort-selected' : ''} ${
								option.sortType === sortOptionType.FAVORITES && !hasFavorites ? 'disabled' : ''
							}`}
							onClick={() => {
								handleOptionClick(option);
							}}
						>
							{option.name}{' '}
							{option.id === currentSortBy?.id && <i className='ri-check-fill selected-icon'></i>}
						</span>
					))}
				</div>
			)}
		</div>
	);
};

export default SortProductsComponent;
